import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox,
  Button,
  Paper,
  Stack,
  Skeleton,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BusinessIcon from "@mui/icons-material/Business";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import api from "../../../../conf/api";
import { FeedType } from "../../Enums/enums";

function BrandFeeds({ brand, selectedFeedIds, decided, onFeedsChange, onFeedsInfoChange }) {
  const [feeds, setFeeds] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Read when the feeds arrive, not when the request started.
  const decidedRef = useRef(decided);
  decidedRef.current = decided;

  useEffect(() => {
    setLoading(true);
    api
      .get(`/mrss/feeds/?brand=${encodeURIComponent(brand.name)}&is_active=true&page_size=100`)
      .then((res) => {
        const data = res.data?.results ?? res.data ?? [];
        setFeeds(data);
        onFeedsInfoChange(brand.name, data);
        // Every feed starts ticked (user request 2026-10-06); a choice already
        // made for this brand — unticking, a restored draft — is kept.
        if (!decidedRef.current) onFeedsChange(brand.name, data.map((f) => f.id));
      })
      .catch(() => setError("Failed to load feeds"))
      .finally(() => setLoading(false));
  }, [brand.name]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleFeed = (feedId) => {
    const next = selectedFeedIds.includes(feedId)
      ? selectedFeedIds.filter((id) => id !== feedId)
      : [...selectedFeedIds, feedId];
    onFeedsChange(brand.name, next);
  };

  const selectAll = () => onFeedsChange(brand.name, (feeds || []).map((f) => f.id));
  const deselectAll = () => onFeedsChange(brand.name, []);

  const socialFeeds = (feeds || []).filter((f) => f.feed_type === FeedType.SOCIAL);
  const customFeeds = (feeds || []).filter((f) => f.feed_type === FeedType.PARTNER);

  const renderGroup = (list, title, chipLabel, color, titleColor) => (
    <Box mb={1.5}>
      <Typography
        variant="caption"
        color={titleColor}
        fontWeight={700}
        sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
          gap: 1,
          mt: 0.5,
        }}
      >
        {list.map((feed) => {
          const checked = selectedFeedIds.includes(feed.id);
          return (
            <FormControlLabel
              key={feed.id}
              sx={{
                m: 0,
                minHeight: 44,
                pr: 1,
                minWidth: 0,
                border: "1px solid",
                borderColor: checked ? `${color}.main` : "divider",
                borderRadius: 1.5,
                bgcolor: checked ? (t) => alpha(t.palette[color].main, 0.08) : "background.paper",
                "&:focus-within": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: 1,
                },
              }}
              control={
                <Checkbox
                  checked={checked}
                  onChange={() => toggleFeed(feed.id)}
                  color={color}
                  sx={{ p: 1.25 }}
                />
              }
              label={
                <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                  <RssFeedIcon fontSize="small" color="action" />
                  <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                    {feed.name}
                  </Typography>
                  <Chip label={chipLabel} size="small" color={color} variant="outlined" />
                </Box>
              }
            />
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ mt: 1, pl: { xs: 1, sm: 1.5 }, borderLeft: "3px solid", borderColor: "primary.light" }}>
      <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} mb={1}>
        <Typography variant="body2" fontWeight={600} color="text.secondary">
          Feeds for {brand.name}
        </Typography>
        {feeds !== null && (
          <Chip
            icon={selectedFeedIds.length > 0 ? <CheckCircleIcon /> : undefined}
            label={`${selectedFeedIds.length} / ${feeds.length} selected`}
            size="small"
            color={selectedFeedIds.length > 0 ? "success" : "default"}
          />
        )}
        {loading && <CircularProgress size={14} aria-label="Loading feeds" />}
      </Box>

      {loading && feeds === null && <Skeleton variant="rounded" height={44} sx={{ mb: 1 }} />}

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {feeds !== null && feeds.length === 0 && (
        <Alert severity="warning" icon={<RssFeedIcon />} sx={{ mb: 1 }}>
          No active feeds found for {brand.name}.
        </Alert>
      )}

      {feeds !== null && feeds.length > 0 && (
        <>
          <Stack direction="row" gap={1} mb={1.5} flexWrap="wrap">
            <Button
              size="small"
              variant="outlined"
              onClick={selectAll}
              sx={{ minHeight: { xs: 44, sm: 36 }, flex: { xs: 1, sm: "0 0 auto" } }}
            >
              Select all
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={deselectAll}
              sx={{ minHeight: { xs: 44, sm: 36 }, flex: { xs: 1, sm: "0 0 auto" } }}
            >
              Deselect all
            </Button>
          </Stack>

          {socialFeeds.length > 0 && renderGroup(socialFeeds, "Social Feeds", "SOCIAL", "primary", "primary")}
          {customFeeds.length > 0 &&
            renderGroup(customFeeds, "Custom Feeds", "PARTNER", "warning", "warning.dark")}

          {selectedFeedIds.length === 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Select at least one feed for {brand.name}.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
}

export default function Step1BrandSelection({
  selectedBrands,
  onBrandsSelect,
  selectedFeeds,
  onFeedsChange,
  onFeedsInfoChange,
}) {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [rowCount, setRowCount] = useState(0);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    setLoading(true);
    api
      .get(`/mrss/brands/?page=${paginationModel.page + 1}&page_size=${paginationModel.pageSize}`)
      .then((res) => {
        setBrands(res.data?.results ?? res.data ?? []);
        setRowCount(res.data?.count ?? (res.data?.length ?? 0));
      })
      .catch(() => setError("Failed to load brands"))
      .finally(() => setLoading(false));
  }, [paginationModel]);

  const columns = [
    {
      field: "name",
      headerName: "Brand",
      flex: 1.5,
      minWidth: 160,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
          <BusinessIcon color="primary" fontSize="small" />
          <Box minWidth={0}>
            <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
            {row.description && (
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {row.description}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      field: "default_language",
      headerName: "Language",
      width: 120,
      renderCell: ({ row }) => row.default_language?.name || "—",
    },
    {
      field: "disabled",
      headerName: "Status",
      width: 90,
      renderCell: ({ row }) => (
        <Typography variant="caption" color={row.disabled ? "error.main" : "success.main"}>
          {row.disabled ? "Disabled" : "Active"}
        </Typography>
      ),
    },
  ];

  const selectedNames = selectedBrands.map((b) => b.name);

  const handleSelectionChange = (newSelection) => {
    const selected = brands.filter((b) => newSelection.includes(b.name));
    const fromOtherPages = selectedBrands.filter((b) => !brands.find((rb) => rb.name === b.name));
    onBrandsSelect([...fromOtherPages, ...selected]);
  };

  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Step 1 — Select Brands & Feeds
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Select one or more brands, then choose which feeds to publish to for each.
      </Typography>

      <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 1.5, sm: 2, md: 3 }, mb: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          gap={1}
          mb={1.5}
        >
          <Typography variant="subtitle1" fontWeight={600}>Brands</Typography>
          <Chip
            icon={selectedBrands.length > 0 ? <CheckCircleIcon /> : undefined}
            label={`${selectedBrands.length} brand${selectedBrands.length !== 1 ? "s" : ""} selected`}
            size="small"
            color={selectedBrands.length > 0 ? "primary" : "default"}
            aria-live="polite"
          />
        </Stack>
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <DataGrid
            rows={brands}
            columns={columns}
            loading={loading}
            rowCount={rowCount}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            getRowId={(r) => r.name}
            checkboxSelection
            rowSelectionModel={selectedNames}
            onRowSelectionModelChange={handleSelectionChange}
            columnVisibilityModel={{ default_language: !isXs, disabled: !isXs }}
            sx={{
              "& .MuiDataGrid-row": { cursor: "pointer" },
              "& .MuiDataGrid-row.Mui-selected": {
                bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
              },
              "& .MuiDataGrid-cell:focus-visible, & .MuiDataGrid-columnHeader:focus-visible": {
                outline: "2px solid",
                outlineColor: "primary.main",
                outlineOffset: -2,
              },
            }}
            autoHeight
            disableColumnMenu
            rowHeight={56}
          />
        </Box>
      </Paper>

      {selectedBrands.length > 0 && (
        <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Typography variant="subtitle1" fontWeight={600}>Feeds</Typography>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            Select which feeds to publish to for each selected brand:
          </Typography>
          {selectedBrands.map((brand) => (
            <Box key={brand.name} mb={2}>
              <Box display="flex" alignItems="center" gap={1} mb={0.5} minWidth={0}>
                <BusinessIcon color="primary" fontSize="small" />
                <Typography variant="body2" fontWeight={700} noWrap>{brand.name}</Typography>
              </Box>
              <BrandFeeds
                brand={brand}
                selectedFeedIds={selectedFeeds[brand.name] || []}
                decided={Array.isArray(selectedFeeds[brand.name])}
                onFeedsChange={onFeedsChange}
                onFeedsInfoChange={onFeedsInfoChange}
              />
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );
}
